const MODE_NORMAL_PAGE = 0x00
const MODE_MODAL_SHOWING = 0x01
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])'
].join(',')

// Open modals, bottom to top. Only the topmost one makes the rest of the page
// inert, so stacked modals never compete for focus.
const openModals = []

export default {
  props: {
    isShowing: {
      type: Boolean,
      required: true
    },
    backgroundEscape: {
      type: Boolean,
      default: true
    }
  },

  beforeRouteLeave() {
    // If we leave the current this.$route we want to make sure that any
    // modals that were open get closed.
    this.pageMode(MODE_NORMAL_PAGE)
  },

  watch: {
    isShowing: function (val) {
      if (val) {
        this.pageMode(MODE_MODAL_SHOWING)
        this.activateFocusTrap()
      } else {
        this.deactivateFocusTrap()
        this.pageMode(MODE_NORMAL_PAGE)
      }
    }
  },

  data() {
    // We keep track of this component instances page mode. That doesn't mean
    // that we know whether another modal instance is open. This is used to track
    // and page mode changes being idempotent and prevent doing work that is not
    // necessary or that could conflict with another modal.
    return {
      currentPageMode: MODE_NORMAL_PAGE
    }
  },

  mounted() {
    const modal = this.$refs.modal

    if (!modal) {
      throw new Error('Could not find modal. Make sure you set ref="modal" on the <div> that has class="modal".')
    }

    if (this.backgroundEscape) {
      // Clicking on the background around the outside of the modal window
      this._clickHandler = (event) => {
        if (event.target === modal) {
          this.closeModal()
        }
      }
    }

    // Hitting the escape key
    this._keydownHandler = (event) => {
      if (event.key === 'Escape' && this.isShowing) {
        this.closeModal()
      }
    }

    modal.addEventListener('click', this._clickHandler)
    document.body.addEventListener('keydown', this._keydownHandler)

    if (this.isShowing) {
      this.pageMode(MODE_MODAL_SHOWING)
      this.activateFocusTrap()
    }
  },

  beforeUnmount() {
    // If something destroys this modal (which means it's no longer being rendered)
    // we want to return the body to the normal state. Without this the modal will
    // vanish but the background and modal setup will still remain.
    this.deactivateFocusTrap()
    this.pageMode(MODE_NORMAL_PAGE)

    this.$refs.modal.removeEventListener('click', this._clickHandler)
    document.body.removeEventListener('keydown', this._keydownHandler)
  },

  methods: {
    pageMode(mode) {
      // Check what the current page mode is and make the operation a noop
      // to prevent any race conditions with other modals showing/hiding in
      // other views.
      if (this.currentPageMode === mode) {
        return
      }

      const body = document.body
      const modal = this.$refs.modal

      if (!modal) return

      this.currentPageMode = mode

      if (mode === MODE_NORMAL_PAGE) {
        body.style.overflow = 'visible'
        modal.style.visibility = 'hidden'
        modal.style.opacity = 0
      }

      if (mode === MODE_MODAL_SHOWING) {
        body.style.overflow = 'hidden'
        modal.style.visibility = 'visible'
        modal.style.opacity = 1
      }
    },

    closeModal() {
      this.$emit('close-modal')
    },

    activateFocusTrap() {
      const modal = this.$refs.modal
      if (!modal || openModals.includes(this)) return

      this._previouslyFocused = document.activeElement

      const top = openModals[openModals.length - 1]
      if (top) top.releaseBackground()
      openModals.push(this)
      this.inertBackground()

      this.$nextTick(() => this.focusFirstElement())
    },

    deactivateFocusTrap() {
      const index = openModals.indexOf(this)
      if (index === -1) return

      const wasTop = index === openModals.length - 1
      this.releaseBackground()
      openModals.splice(index, 1)

      // A modal closing underneath another leaves the top one in charge of focus.
      // But the top one may have opened from inside this one: closing the upsell
      // re-shows the icon detail modal first, which records the upsell's Close
      // button as its opener. That button is hidden once this one closes, so a
      // later Escape sent focus to <body>. Hand over this modal's own opener.
      if (!wasTop) {
        const modal = this.$refs.modal
        openModals.forEach((other) => {
          if (modal && other._previouslyFocused && modal.contains(other._previouslyFocused)) {
            other._previouslyFocused = this._previouslyFocused
          }
        })
        this._previouslyFocused = null
        return
      }

      const top = openModals[openModals.length - 1]
      if (top) top.inertBackground()

      const previous = this._previouslyFocused
      this._previouslyFocused = null
      if (previous && typeof previous.focus === 'function' && document.contains(previous)) {
        previous.focus()
      }
    },

    // Modals render inline rather than at the body root, so inert the siblings
    // of every ancestor on the way up instead of just the body's children.
    inertBackground() {
      this._inertedElements = []

      for (let node = this.$refs.modal; node && node !== document.body; node = node.parentElement) {
        const parent = node.parentElement
        if (!parent) break

        for (const sibling of parent.children) {
          // Skip anything already inert so we never undo someone else's.
          if (sibling !== node && !sibling.hasAttribute('inert')) {
            sibling.setAttribute('inert', '')
            this._inertedElements.push(sibling)
          }
        }
      }
    },

    releaseBackground() {
      if (!this._inertedElements) return
      this._inertedElements.forEach((el) => el.removeAttribute('inert'))
      this._inertedElements = null
    },

    focusFirstElement() {
      const modal = this.$refs.modal
      if (!modal) return

      // Lets a modal pick its initial focus when the first control isn't the right one.
      const first =
        modal.querySelector('[data-autofocus]') ||
        Array.from(modal.querySelectorAll(FOCUSABLE_SELECTOR)).find((el) => el.offsetParent !== null)

      if (first) {
        first.focus()
      } else {
        if (!modal.hasAttribute('tabindex')) modal.setAttribute('tabindex', '-1')
        modal.focus()
      }
    }
  }
}
